import { CustomModal } from "@/components/custom-modal";
import {
  ConfirmPasswordError,
  PasswordError,
} from "@/components/PasswordError";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormControl,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { cn } from "@/lib/utils";
import { ChangeInAppPassword } from "@/services/Settings.service";
import { useMutation } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeClosed,
  Loader2,
  LockKeyholeIcon,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ToastMessage, type } from "@/lib/toastr";

const InAppChangePassword = () => {
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);

  const [step, setStep] = useState(1);
  const form = useForm();
  const mutation = useMutation({
    mutationFn: (v) => ChangeInAppPassword(v),
    mutationKey: ["changePassword"],
    onSuccess: async (data) => {
      setStep(2);
      ToastMessage(type.SUCCESS, data?.data?.data?.message);
    },
    onError: (err: any) => {
      ToastMessage(type.ERROR, err?.response?.data?.message);
    },
  });
  const onSubmit = async (data: any) => {
    await mutation.mutate(data);
  };
  return (
    <CustomModal
      button={
        <Button
          variant="outline"
          className="w-40 bg-transparent text-primary0 border-primary0 hover:text-primary0"
        >
          Click to Change <ChevronRight />
        </Button>
      }
      size="sm"
    >
      {step === 1 ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-8">
              <DialogHeader className="flex flex-row  w-full p-0 items-center gap-4 bg-off-white-1 py-6 px-8 rounded-t-[16px]">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-off-white-3">
                  <LockKeyholeIcon />
                </div>
                <div className="flex flex-col ">
                  <DialogTitle className="text-sm font-normal capitalize text-primary-3">
                    Change Password
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    Please create a new Password
                  </DialogDescription>
                </div>
              </DialogHeader>
              <div className="flex flex-col gap-8 px-8 pb-8">
                <div className="grid gap-2">
                  <p className="text-base font-medium text-center text-gray-1">
                    Enter New Password
                  </p>
                  <p className="text-sm text-center text-gray-80">
                    This password will be used to log in to your account
                    subsequently
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="oldPassword"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <div
                          className={cn(
                            "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                            form.formState.errors?.password === undefined
                              ? "border-input-border"
                              : "border-destructive"
                          )}
                        >
                          <div className="flex flex-col w-full">
                            <FormLabel className="text-xs">
                              Old Password
                            </FormLabel>

                            <FormControl>
                              <Input
                                type={!show ? "password" : "text"}
                                placeholder=""
                                className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                {...field}
                              />
                            </FormControl>
                          </div>
                          <div
                            className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                            onClick={() => setShow(!show)}
                          >
                            {!show ? (
                              <EyeClosed />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <div
                          className={cn(
                            "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                            form.formState.errors?.password === undefined
                              ? "border-input-border"
                              : "border-destructive"
                          )}
                        >
                          <div className="flex flex-col w-full">
                            <FormLabel className="text-xs">
                              New Password
                            </FormLabel>

                            <FormControl>
                              <Input
                                type={!show2 ? "password" : "text"}
                                placeholder=""
                                className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                {...field}
                              />
                            </FormControl>
                          </div>
                          <div
                            className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                            onClick={() => setShow2(!show2)}
                          >
                            {!show2 ? (
                              <EyeClosed />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </FormItem>
                    );
                  }}
                />
                <PasswordError
                  passwordErrorMin={form.watch("newPassword")}
                  passwordErrorLowerCaseChar={form.watch("newPassword")}
                  passwordErrorUpperCaseChar={form.watch("newPassword")}
                  passwordErrorHasNumber={form.watch("newPassword")}
                  passwordErrorSpecialChar={form.watch("newPassword")}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <div
                          className={cn(
                            "flex w-full items-center gap-1.5 border-[1px]  rounded-[10px] px-4 py-2 h-14",
                            form.formState.errors?.password === undefined
                              ? "border-input-border"
                              : "border-destructive"
                          )}
                        >
                          <div className="flex flex-col w-full">
                            <FormLabel className="text-xs">
                              Confirm New Password
                            </FormLabel>

                            <FormControl>
                              <Input
                                type={!show2 ? "password" : "text"}
                                placeholder=""
                                className="w-full h-6 px-0 py-0 border-none outline-none placeholder:py-0 placeholder:text-sm placeholder:font-medium ring-transparent focus-visible:ring-transparent focus:border-none"
                                {...field}
                              />
                            </FormControl>
                          </div>
                          <div
                            className=" left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                            onClick={() => setShow2(!show2)}
                          >
                            {!show2 ? (
                              <EyeClosed />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </FormItem>
                    );
                  }}
                />
                <ConfirmPasswordError
                  password={form.watch("newPassword")}
                  confirmNewPassword={form.watch("confirmPassword")}
                />
              </div>
            </div>
            <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
                >
                  <ChevronLeft /> Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
                disabled={
                  !form.formState.isDirty ||
                  !form.formState.isValid ||
                  form.formState.isSubmitting ||
                  mutation?.isPending
                }
              >
                {mutation?.isPending && (
                  <Loader2 className="text-white animate-spin" />
                )}
                Proceed <ChevronRight />
              </Button>
            </DialogFooter>
          </form>
        </Form>
      ) : (
        <div className="flex flex-col items-center justify-center gap-10 p-8">
          <div className="flex flex-col gap-9">
            <DialogHeader className="flex flex-col items-center text-center ">
              <svg
                width="64"
                height="64"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M58.6654 30.8445C58.6654 44.933 46.7246 56.3557 31.9987 56.3557C30.2672 56.3581 28.5406 56.1978 26.8398 55.8786C25.6156 55.6485 25.0034 55.5335 24.5761 55.5989C24.1488 55.6642 23.5432 55.9863 22.332 56.6303C18.9057 58.4525 14.9106 59.0959 11.0683 58.3813C12.5287 56.585 13.526 54.4298 13.9661 52.1194C14.2328 50.7061 13.572 49.3333 12.5824 48.3282C8.08758 43.7639 5.33203 37.6135 5.33203 30.8445C5.33203 16.7561 17.2728 5.33325 31.9987 5.33325C46.7246 5.33325 58.6654 16.7561 58.6654 30.8445Z"
                  fill="#F1EFF1"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M56.6654 30.8444C56.6654 43.7442 45.7046 54.3532 32.0011 54.3556H31.996C30.3896 54.3578 28.7875 54.2093 27.2088 53.9127L27.1627 53.9042C26.5903 53.7967 26.0899 53.7026 25.681 53.6466C25.2718 53.5903 24.7802 53.5444 24.2741 53.6218C23.7544 53.7012 23.2745 53.9082 22.8899 54.0908C22.7245 54.1695 22.5435 54.2602 22.3473 54.3615C22.0719 54.5034 21.7666 54.6658 21.4331 54.8431L21.3929 54.8644C19.2768 55.9898 16.9169 56.606 14.5333 56.6623C15.1823 55.3508 15.6537 53.9482 15.9308 52.4935L15.9314 52.49C15.9791 52.2378 16.0081 51.9887 16.0202 51.7436C16.1179 49.7765 15.1319 48.0669 14.0074 46.925C9.85723 42.7106 7.33203 37.0554 7.33203 30.8444C7.33203 29.6546 7.42528 28.4842 7.60531 27.3397C9.37731 16.0763 19.557 7.33325 31.9987 7.33325C32.4144 7.33325 32.8275 7.34304 33.2379 7.36237C34.3414 7.41437 35.2779 6.56208 35.3299 5.45875C35.3819 4.35539 34.5296 3.4188 33.4262 3.36683C32.9531 3.34453 32.4771 3.33325 31.9987 3.33325C16.2526 3.33325 3.33203 15.569 3.33203 30.8444C3.33203 38.1716 6.31792 44.8172 11.1573 49.7314C11.8716 50.4567 12.1038 51.2002 12.001 51.7474C11.6216 53.737 10.7639 55.5853 9.51648 57.1197C9.07134 57.6671 8.94766 58.4087 9.19104 59.0711C9.43443 59.7332 10.0089 60.2183 10.7026 60.3474C14.9988 61.1466 19.4531 60.4266 23.271 58.3962C23.8938 58.065 24.3006 57.8492 24.606 57.7042C24.7505 57.6354 24.8384 57.5996 24.8837 57.5823C24.9275 57.585 25.0078 57.5916 25.1373 57.6095C25.4363 57.6503 25.8381 57.7253 26.4706 57.8442C28.2928 58.1863 30.1456 58.358 32.0014 58.3556C47.7462 58.3543 60.6654 46.1191 60.6654 30.8444C60.6654 30.2964 60.6486 29.7519 60.6155 29.2114C60.5483 28.1087 59.6 27.2695 58.4974 27.337C57.395 27.4042 56.5558 28.3527 56.623 29.4551C56.6512 29.9146 56.6654 30.3778 56.6654 30.8444Z"
                  fill="#005229"
                />
                <path
                  d="M32.0431 31.1222C32.0512 31.1096 32.0598 31.097 32.0683 31.0842C32.4086 30.5736 32.9024 29.8452 33.5257 28.9623C33.6516 28.784 33.7827 28.5993 33.9193 28.4087L26.6719 26.2224L32.0431 31.1222Z"
                  fill="#005229"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M56.0056 14.5139C57.3937 14.0222 58.1208 12.4983 57.6291 11.1101C57.1373 9.72187 55.6135 8.99507 54.2251 9.48672C51.2563 10.5383 48.3707 12.5643 45.7887 14.8287C43.1791 17.1172 40.7286 19.7842 38.637 22.2917C36.7519 24.5513 35.1317 26.7135 33.9193 28.4083C32.6991 26.7501 31.4874 25.635 30.3731 24.8922C29.4746 24.2933 28.6672 23.9553 28.0155 23.7692C27.6916 23.6767 27.4118 23.6231 27.1839 23.5928C27.0704 23.5776 26.9702 23.5683 26.8845 23.5628C26.8415 23.56 26.8024 23.5582 26.7668 23.557L26.7163 23.5559L26.6936 23.5556L26.6825 23.5555H26.6772C26.6772 23.5555 26.6719 23.5555 26.6719 26.222L33.9193 28.4083C33.7827 28.599 33.6515 28.7837 33.5257 28.9619C32.9024 29.8449 32.4086 30.5732 32.0683 31.0838L32.0431 31.1218L36.0337 34.7623L36.0383 34.7555L36.0589 34.7229L36.1482 34.5853C36.2278 34.4623 36.3487 34.2781 36.5069 34.0404C36.8237 33.565 37.2905 32.8767 37.8829 32.0375C39.0693 30.3563 40.7507 28.0831 42.7321 25.7081C44.7186 23.3269 46.9738 20.883 49.3051 18.8383C51.6641 16.7695 53.9552 15.2401 56.0056 14.5139Z"
                  fill="#005229"
                />
                <path
                  d="M33.7776 33.3336C35.946 34.7114 36.0285 34.7631 36.0285 34.7631L32.0379 31.1226C31.88 31.36 31.7567 31.5481 31.671 31.6802L31.5668 31.8414L31.5387 31.8853L31.5273 31.9029C31.5273 31.9029 31.5266 31.9038 33.7776 33.3336Z"
                  fill="#005229"
                />
                <path
                  d="M33.7774 33.3331C31.5265 31.9032 31.5272 31.9023 31.5272 31.9023L31.5386 31.8848L31.5667 31.8409L31.6709 31.6797C31.7565 31.5476 31.8799 31.3594 32.0378 31.122L26.6666 26.2222V23.5557C25.1939 23.5557 24 24.7495 24 26.2222C24 27.6357 25.0997 28.7923 26.4902 28.8831C26.503 28.886 26.5215 28.8906 26.545 28.8974C26.6712 28.9334 26.9748 29.0399 27.4097 29.3298C28.2743 29.9063 29.773 31.2866 31.3925 34.5256C31.8223 35.3852 32.6813 35.9471 33.6409 35.9962C34.6005 36.0453 35.5132 35.5735 36.0284 34.7625C36.0284 34.7625 35.9459 34.7108 33.7774 33.3331Z"
                  fill="#005229"
                />
              </svg>

              <div className="flex flex-col gap-2">
                <DialogTitle className="text-center text-green-3">
                  Password Changed Successfully!
                </DialogTitle>
                <DialogDescription className="text-center">
                  You have successfully changed your password. That will be the
                  new password to log in.
                </DialogDescription>
              </div>
            </DialogHeader>
          </div>
          <div className="flex flex-col items-center w-full ">
            <DialogClose asChild>
              <Button
                className="flex justify-center items-center bg-off-white-2 h-12 rounded-[10px] w-full text-gray"
                onClick={() => setStep(1)}
              >
                <ChevronLeft /> Back to settings
              </Button>
            </DialogClose>
          </div>
        </div>
      )}
    </CustomModal>
  );
};

export default InAppChangePassword;
